// 계약 관리 — 목록·90일 내·달력 보기(C3b · 분류·부서·검색) + 편집 등급의 «계약 등록»(C2b)·«내보내기»(C3)·«휴지통»(C2c1)·«설정»(C2c2).
// 메뉴 노출·URL 가드는 utils/menuAccess(시스템 관리자 + 명시 허용) · 실제 차단은 서버(require_contract_level).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconFileInvoice, IconSearch } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import Pager from '../components/Pager';
import { alertDialog } from '../components/AppDialog';
import { Page, PageHeader, Button, PillTabs } from '../components/ui';
import { contractsAPI } from '../api/client';
import { COLORS, ICON_STROKE, card, input } from '../utils/uiStyles';
import { kstDaysAgo, kstToday } from '../utils/kstTime';
import ContractFormModal from './contracts/ContractFormModal';
import ContractsCalendar from './contracts/ContractsCalendar';
import { CATEGORY_LABELS, daysText, dueText, errorText, isUrgent, labelOf } from './contracts/contractLabels';

const PAGE_SIZE = 50;
// «90일 내»는 쪽 나눔 없이 한 표 — 목록 API 상한(limit ≤ 1000)까지 받고, 넘으면 안내만 한다.
const DUE_LIMIT = 1000;
const VIEWS = [['list', '목록'], ['due90', '90일 내'], ['calendar', '달력']];
const HEADERS = ['계약번호', '분류', '계약명', '상대방', '부서', '담당자', '기준일', '남은 날', '자동연장'];
const th = { height: '33px', padding: '5px 9px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: `var(--gw-muted, ${COLORS.textMuted})`, whiteSpace: 'nowrap', borderBottom: `1px solid var(--gw-line, ${COLORS.borderSoft})` };
const td = { height: '41px', padding: '7px 9px', fontSize: '13px', color: `var(--gw-secondary, ${COLORS.textSub})`, borderBottom: `1px solid var(--gw-line, ${COLORS.borderSoft})`, whiteSpace: 'nowrap' };
const linkBtn = { padding: 0, border: 'none', background: 'none', color: COLORS.primaryDark, cursor: 'pointer', fontSize: '13px', fontWeight: 600, textAlign: 'left' };
// 열 클래스(HEADERS 와 같은 순서) — 폭 ≤768 에서 ct-col-m 5열을 숨기고 남은 4열을 % 폭으로 고정한다(아래 TABLE_CSS).
//    768 = 모바일 확대 1.5배 구간(601~768)까지 덮는 경계. 숨긴 열은 넓은 화면·내보내기 파일에 그대로 있다.
const CONTRACT_COL_CLASS = ['ct-col-m', 'ct-col-m', 'ct-col-name', 'ct-col-party', 'ct-col-m', 'ct-col-m', 'ct-col-due', 'ct-col-days', 'ct-col-m'];
const TABLE_CSS = '@media (max-width:768px){.ct-table{min-width:0!important;table-layout:fixed}.ct-table .ct-col-m{display:none}'
  + '.ct-table th,.ct-table td{white-space:normal!important;word-break:break-word}.ct-table th{word-break:keep-all}'
  + '.ct-table .ct-col-name{width:40%}.ct-table .ct-col-party{width:24%}.ct-table .ct-col-due{width:18%}.ct-table .ct-col-days{width:18%}}';
// 조건 줄 높이 = md 단추(31)와 같게.
const FIELD_H = '31px';
const text = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// 알림 실패 안내(C4b) — 마지막 실행이 실패이고 날짜가 없거나 KST 어제·오늘이면 글, 아니면 null.
//    last_run.date 는 'YYYY-MM-DD'(서버 contract_notify._finish) → «M월 D일».
function notifyFailureText(lastRun) {
  if (!lastRun || lastRun.ok !== false) return null;
  const reason = lastRun.detail ? ` — ${lastRun.detail}` : '';
  const date = typeof lastRun.date === 'string' ? lastRun.date : '';
  if (!date) return `최근 알림을 보내지 못했습니다${reason}`;
  if (date !== kstToday() && date !== kstDaysAgo(1)) return null;
  const [, month, day] = date.split('-');
  return `${Number(month)}월 ${Number(day)}일 알림을 보내지 못했습니다${reason}`;
}

export default function ContractsPage() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [departmentsFailed, setDepartmentsFailed] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [notifyFailure, setNotifyFailure] = useState(null);   // 알림 실패 안내 글(시스템 관리자만)
  const [createForm, setCreateForm] = useState(null);   // 열린 등록 창의 부서 목록 { departments, departmentsFailed }
  const [category, setCategory] = useState('');
  const [department, setDepartment] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);   // 등록 창을 열려고 부서를 다시 읽는 중
  const [view, setView] = useState('list');   // 'list' | 'due90' | 'calendar'
  // «90일 내» 결과는 목록과 따로 둔다 — 보기를 오가도 서로 덮지 않게.
  const [dueData, setDueData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);
  // 등급 글자는 내보내기(C3)용으로 읽어 둔다 · 등록·휴지통 단추는 다시 그려야 하므로 canEdit 상태로 둔다.
  const levelRef = useRef('view');
  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const openingRef = useRef(false);
  // 부서 읽기 번호 — 마운트 읽기가 늦게 와서 등록 창의 다시 읽기 결과를 덮지 않게, 최신 번호의 응답만 쓴다.
  const deptSeqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    // 등급 읽기가 실패하면 열람(view)으로 본다 — 단추를 숨기는 쪽으로 틀린다.
    contractsAPI.me()
      .then((r) => {
        levelRef.current = r.data?.level || 'view';
        if (mountedRef.current) setCanEdit(r.data?.can?.edit === true);
        // 🔴 알림 설정 읽기는 이 사슬에 잇지 않는다(return 하지 않고 자기 catch) — 이 읽기 하나가 거절돼도
        //    아래 catch 가 canEdit 을 꺼서 관리자 단추가 사라지면 안 된다. 실패하면 안내만 안 뜬다.
        if (mountedRef.current && r.data?.can?.settings === true) {
          Promise.resolve()
            .then(() => contractsAPI.notifySettings())
            .then((n) => { if (mountedRef.current) setNotifyFailure(notifyFailureText(n?.data?.last_run)); })
            .catch(() => {});
        }
      })
      .catch(() => {
        levelRef.current = 'view';
        if (mountedRef.current) setCanEdit(false);
      });
    // 실패와 «부서 0개»를 가른다 — 등록 창을 열 때 다시 읽을지 정하는 데 쓴다.
    const deptSeq = deptSeqRef.current + 1;
    deptSeqRef.current = deptSeq;
    const isDeptCurrent = () => mountedRef.current && deptSeqRef.current === deptSeq;
    contractsAPI.departments()
      .then((r) => {
        if (!isDeptCurrent()) return;
        setDepartments(Array.isArray(r.data) ? r.data : []);
        setDepartmentsFailed(false);
      })
      .catch(() => { if (isDeptCurrent()) setDepartmentsFailed(true); });
    return () => {
      mountedRef.current = false;
      requestSeqRef.current += 1;   // 화면을 떠난 뒤 온 응답은 버린다
    };
  }, []);

  useEffect(() => {
    // 🔴 보기 가드는 번호를 올리기 «전» — 다른 보기에서 번호를 올리면 그 보기의 응답이 버려진다.
    if (view !== 'list') return;
    // 늦게 온 응답 버리기 — 요청마다 번호를 올리고, 응답 때 최신 번호가 아니면 버린다.
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    const isCurrent = () => mountedRef.current && requestSeqRef.current === seq;
    const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
    if (category) params.category = category;
    if (department) params.department = department;
    if (q) params.q = q;
    setBusy(true);
    setError('');
    contractsAPI.list(params)
      .then((r) => {
        if (!isCurrent()) return;
        setData({ items: r.data?.items || [], total: Number(r.data?.total) || 0 });
      })
      .catch(async (err) => {
        const message = await errorText(err, '목록을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setData(null);
        setError(message);
      })
      .finally(() => { if (isCurrent()) setBusy(false); });
  }, [view, category, department, q, page, reload]);

  // «90일 내» — 기준일이 KST 오늘~오늘+90일인 계약을 기준일 순(서버 순서)으로 한 번에 읽는다.
  //    🔴 목록 응답이 오기 전에 이 보기로 오면 번호가 올라가 목록의 finally 가 busy 를 못 푼다 → 여기서 직접 푼다.
  useEffect(() => {
    if (view !== 'due90') return;
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    const isCurrent = () => mountedRef.current && requestSeqRef.current === seq;
    const params = { limit: DUE_LIMIT, offset: 0, due_from: kstToday(), due_to: kstDaysAgo(-90) };
    if (category) params.category = category;
    if (department) params.department = department;
    if (q) params.q = q;
    setBusy(true);
    setError('');
    contractsAPI.list(params)
      .then((r) => {
        if (!isCurrent()) return;
        setDueData({ items: r.data?.items || [], total: Number(r.data?.total) || 0 });
      })
      .catch(async (err) => {
        const message = await errorText(err, '목록을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setDueData(null);
        setError(message);
      })
      .finally(() => { if (isCurrent()) setBusy(false); });
  }, [view, category, department, q, reload]);

  // 보기 바꾸기 — 앞 보기의 경고·읽기 중 표시를 남기지 않는다.
  //    «달력»은 자기 읽기·경고를 따로 쓰므로, 앞 보기의 늦은 응답은 번호를 올려 버린다.
  const changeView = (next) => {
    if (next === view) return;
    setError('');
    if (next === 'calendar') {
      requestSeqRef.current += 1;
      setBusy(false);
    }
    setView(next);
  };

  // 필터를 바꾸면 첫 쪽으로.
  const changeCategory = (value) => { setCategory(value); setPage(1); };
  const changeDepartment = (value) => { setDepartment(value); setPage(1); };
  const applySearch = () => { setQ(qInput.trim()); setPage(1); setReload((n) => n + 1); };
  const openContract = (id) => navigate(`/contracts/${id}`);

  // 등록 창 — 부서 목록이 비었거나 읽기에 실패해 있었으면 그 자리에서 한 번 다시 읽는다.
  //    그래도 실패면 창이 안내 글을 보이고 저장을 끈다(부서는 필수라 고를 수 없으면 등록할 수 없다).
  const openCreate = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    let list = departments;
    let failed = departmentsFailed;
    if (failed || list.length === 0) {
      setOpening(true);
      const seq = deptSeqRef.current + 1;
      deptSeqRef.current = seq;
      try {
        const r = await contractsAPI.departments();
        list = Array.isArray(r.data) ? r.data : [];
        failed = false;
      } catch {
        failed = true;
      }
      if (mountedRef.current && deptSeqRef.current === seq) {
        if (!failed) setDepartments(list);
        setDepartmentsFailed(failed);
      }
    }
    openingRef.current = false;
    if (mountedRef.current) {
      setOpening(false);
      setCreateForm({ departments: list, departmentsFailed: failed });
    }
  };

  const onCreated = (detail) => {
    setCreateForm(null);
    if (detail && detail.id !== undefined && detail.id !== null) navigate(`/contracts/${detail.id}`);
    else setReload((n) => n + 1);
  };

  // 내보내기(C3) — 서버 /export 는 category·department·q 만 받는다(기간 질의는 어느 보기에서도 싣지 않는다).
  //    «달력»은 분류 고르기를 숨기고 체크 상자로 거르므로 분류도 싣지 않는다. 두 번 누름은 ref 로 먼저 막는다.
  const exportContracts = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    const params = {};
    if (category && view !== 'calendar') params.category = category;
    if (department) params.department = department;
    if (q) params.q = q;
    try {
      const r = await contractsAPI.export(params);
      const disposition = r.headers?.['content-disposition'] || '';
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
      let filename = `계약목록_${kstToday().replaceAll('-', '')}.xlsx`;
      if (encoded) {
        try { filename = decodeURIComponent(encoded.trim()); } catch { /* 기본 이름을 쓴다 */ }
      }
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* 지울 수 없으면 둔다 */ } }, 4000);
    } catch (err) {
      await alertDialog(await errorText(err, '내보내지 못했습니다.'));
    } finally {
      exportingRef.current = false;
      if (mountedRef.current) setExporting(false);
    }
  };

  // 목록·90일 내 두 표가 같이 쓰는 행.
  const renderRow = (it, testId) => (
    <tr key={it.id} className="gw-tr" data-testid={testId} onClick={() => openContract(it.id)} style={{ cursor: 'pointer' }}>
      <td className={CONTRACT_COL_CLASS[0]} style={{ ...td, color: `var(--gw-text, ${COLORS.text})` }}>{text(it.contract_no)}</td>
      <td className={CONTRACT_COL_CLASS[1]} style={td}>{labelOf(CATEGORY_LABELS, it.category)}</td>
      <td className={CONTRACT_COL_CLASS[2]} style={td}>
        {/* 🔴 행까지 올라가 두 번 이동하지 않게 멈춘다(방문 기록이 겹치면 «뒤로»를 두 번 눌러야 한다). */}
        <button type="button" onClick={(e) => { e.stopPropagation(); openContract(it.id); }} style={linkBtn}>{text(it.contract_name)}</button>
      </td>
      <td className={CONTRACT_COL_CLASS[3]} style={td}>{text(it.counterparty)}</td>
      <td className={CONTRACT_COL_CLASS[4]} style={td}>{text(it.department)}</td>
      <td className={CONTRACT_COL_CLASS[5]} style={td}>{text(it.owner_name)}</td>
      <td className={CONTRACT_COL_CLASS[6]} style={td}>{dueText(it)}</td>
      <td className={CONTRACT_COL_CLASS[7]} style={{ ...td, color: isUrgent(it.days_left) ? COLORS.dangerText : COLORS.text, fontWeight: isUrgent(it.days_left) ? 700 : 400 }}>{daysText(it.days_left)}</td>
      <td className={CONTRACT_COL_CLASS[8]} style={td}>{it.auto_renewal ? '예' : '아니오'}</td>
    </tr>
  );

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const dueItems = dueData?.items || [];
  const dueTotal = dueData?.total || 0;

  // 머리 단추 묶음 — 좁은 폭에서 단추가 넘치지 않게 줄을 바꾼다(.gw-ph-actions 자체에는 줄바꿈이 없다).
  //    꺼짐 모양은 공용 .gw-btn:disabled 가 맡는다 · 글자·title·onClick 은 그대로.
  const headActions = (busy || canEdit) ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
      {busy && <span role="status" style={{ color: `var(--gw-muted, ${COLORS.textMuted})`, fontSize: '12px', alignSelf: 'center', whiteSpace: 'nowrap' }}>불러오는 중…</span>}
      {canEdit && (
        <>
          <Button variant="primary" onClick={openCreate} disabled={opening}>{opening ? '여는 중…' : '계약 등록'}</Button>
          <Button variant="secondary" onClick={exportContracts} disabled={exporting}
            title="지금 부서·검색어(목록·90일 내 보기에서는 분류도)로 엑셀 저장">
            {exporting ? '내보내는 중…' : '내보내기'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/contracts/trash')}>휴지통</Button>
          <Button variant="secondary" onClick={() => navigate('/contracts/settings')}>설정</Button>
        </>
      )}
    </div>
  ) : null;
  const fieldLabel = { fontSize: '12px', color: `var(--gw-secondary, ${COLORS.textSub})` };

  return (
    <AppShell app="contracts">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <Page>
          <style>{TABLE_CSS}</style>
          <PageHeader icon={IconFileInvoice} tone="red" title="계약 관리" actions={headActions} />

          {/* 알림 실패 안내 — 공용 경고(error)와 별개 요소 · 보기와 무관하게 보인다. */}
          {notifyFailure && (
            <div role="alert" data-testid="contract-notify-failure" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <span>{notifyFailure}</span>
              <Button variant="secondary" size="sm" onClick={() => navigate('/contracts/settings', { state: { tab: 'notify' } })}>알림 설정 열기</Button>
            </div>
          )}

          <PillTabs ariaLabel="보기" idBase="contracts-view" value={view} onChange={changeView} items={VIEWS.map(([key, label]) => ({ key, label }))} />

          {/* 조건 줄 — 카드 없는 툴바(시안 F). */}
          <section aria-label="계약 검색 조건" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '16px' }}>
            {/* «달력»에서는 분류를 달력의 체크 상자가 거른다 → 고르기를 숨긴다(부서·검색어는 그대로 적용). */}
            {view !== 'calendar' && (
              <label style={fieldLabel}>분류 <select aria-label="분류" value={category} onChange={(e) => changeCategory(e.target.value)} style={{ ...input, width: '170px', height: FIELD_H, marginLeft: '4px' }}>
                <option value="">전체</option>
                {Object.entries(CATEGORY_LABELS).map(([code, name]) => <option key={code} value={code}>{code} {name}</option>)}
              </select></label>
            )}
            <label style={fieldLabel}>부서 <select aria-label="부서" value={department} onChange={(e) => changeDepartment(e.target.value)} style={{ ...input, width: '160px', height: FIELD_H, marginLeft: '4px' }}>
              <option value="">전체</option>
              {departments.map((d) => <option key={d.id ?? d.name} value={d.name}>{d.name}</option>)}
            </select></label>
            {/* 돋보기 상자 — 모양만 공용 SearchInput 과 같게. 🔴 그 부품은 type="text"(textbox)이고 Enter 자리가 없어 쓰지 않는다
                (시험·e2e 가 searchbox «검색» 으로 찾는다). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', height: FIELD_H, padding: '0 9px', boxSizing: 'border-box', border: `1px solid var(--gw-border, ${COLORS.border})`, borderRadius: '5px', background: `var(--gw-surface, ${COLORS.surface})`, width: '240px', maxWidth: '100%' }}>
              <IconSearch size={14} stroke={ICON_STROKE} aria-hidden="true" style={{ color: `var(--gw-muted, ${COLORS.textMuted})`, flexShrink: 0 }} />
              <input type="search" aria-label="검색" placeholder="계약번호·계약명·상대방" value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applySearch(); } }}
                style={{ border: 0, background: 'transparent', flex: 1, minWidth: 0, width: '100%', fontSize: '12px', color: `var(--gw-text, ${COLORS.text})` }} />
            </div>
            <Button variant="primary" onClick={applySearch}>검색</Button>
          </section>

          <div role="tabpanel" id={`contracts-view-panel-${view}`} aria-labelledby={`contracts-view-tab-${view}`}>
            {/* 공용 경고는 목록·90일 내에서만 — «달력»은 달력 안의 자기 경고만 쓴다(경고 두 개 방지). */}
            {error && view !== 'calendar' && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{error}</div>}

            {/* 두 표는 카드로 감싸지 않는다 — 머리줄 아래 선과 행 아래 선만. */}
            {view === 'list' && (
              <section>
                <div style={{ overflowX: 'auto' }}>
                  <table aria-label="계약 목록" className="ct-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
                    <thead><tr>{HEADERS.map((h, i) => <th key={h} className={CONTRACT_COL_CLASS[i]} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {items.map((it) => renderRow(it, 'contract-row'))}
                      {data && !error && items.length === 0 && (
                        <tr><td colSpan={HEADERS.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center', padding: '24px' }}>조건에 맞는 계약이 없습니다.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '10px 0', fontSize: '12px', color: COLORS.textSub }}>총 {total.toLocaleString()}건</div>
                <Pager page={page} totalPages={totalPages} onPage={setPage} />
              </section>
            )}

            {view === 'due90' && (
              <section>
                <div style={{ overflowX: 'auto' }}>
                  <table aria-label="90일 내 기한" className="ct-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
                    <thead><tr>{HEADERS.map((h, i) => <th key={h} className={CONTRACT_COL_CLASS[i]} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {dueItems.map((it) => renderRow(it, 'contract-due-row'))}
                      {dueData && !error && dueItems.length === 0 && (
                        <tr><td colSpan={HEADERS.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center', padding: '24px' }}>90일 안에 기한이 오는 계약이 없습니다.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px 0', fontSize: '12px', color: COLORS.textSub }}>
                  <span>90일 내 {dueTotal.toLocaleString()}건</span>
                  {dueTotal > dueItems.length && <span style={{ color: COLORS.dangerText }}>처음 1,000건만 보입니다 — 분류·부서로 줄여 주세요.</span>}
                </div>
              </section>
            )}

            {view === 'calendar' && (
              <ContractsCalendar department={department} q={q} reloadKey={reload} onOpen={openContract} />
            )}
          </div>
        </Page>
      </main>
      {createForm && (
        <ContractFormModal
          mode="create"
          departments={createForm.departments}
          departmentsFailed={createForm.departmentsFailed}
          onClose={() => setCreateForm(null)}
          onSaved={onCreated}
        />
      )}
    </AppShell>
  );
}
